import React from 'react';
import { TrackingIssueMessage } from '../issues';
import { P2GLink } from '@parcel2go/core-ui';
import { Trans, useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { useTenant } from '@parcel2go/npm-p2g-app';
import { PublicSettings } from '~/lib/ui-serverless-settings';

interface ITrackingCollection {
  orderReference: number;
  hasAddressIssue?: boolean;
  hasAttemptedCollection?: boolean;
  hasCancelled?: boolean;
  isOwner: boolean;
  headingVariant?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
}

const TrackingCollection: React.FC<ITrackingCollection> = (props) => {
  const { t } = useTranslation(TranslationNamespaces.TRACKING);
  const sharedProps = {
    orderReference: props.orderReference,
    showOptions: true
  };

  const { settings } = useTenant();
  const websiteUrl = settings[PublicSettings.WEBSITE];

  return (
    <>
      {props.hasCancelled && (
        <TrackingIssueMessage
          color="info"
          title={t('RESULT_ISSUE_CANCELLED_TITLE', 'The parcel has been cancelled')}
          message={t(
            'RESULT_ISSUE_CANCELLED',
            'The courier notified us that your one or all of your parcel collections is no longer required.'
          )}
          {...sharedProps}
          headingVariant={props.headingVariant}
        />
      )}
      {!props.hasCancelled && props.hasAddressIssue && (
        <TrackingIssueMessage
          color="info"
          title={t('RESULT_COLLECTION_ADDRESS_ISSUE_TITLE', 'The courier had trouble finding the collection address')}
          message={
            <Trans
              t={t}
              i18nKey="RESULT_COLLECTION_ADDRESS_ISSUE"
              defaults="The courier attempted to collect your parcel but could not locate your address. No need to get in touch, we will email you shortly with an update.  <0>To review or edit the collection address click here</0>"
              components={[<P2GLink href={`${websiteUrl}/service-centre/select?caseType=EditAddress`} />]}
            />
          }
          headingVariant={props.headingVariant}
          {...sharedProps}
        />
      )}

      {!props.hasCancelled && props.hasAttemptedCollection && (
        <TrackingIssueMessage
          color="info"
          title={t('RESULT_COLLECTION_ATTEMPTED_TITLE', 'The courier failed to make the collection')}
          message={
            <Trans
              t={t}
              i18nKey="RESULT_COLLECTION_ATTEMPTED"
              defaults="The courier has notified us that they failed to collect the parcel from your address.  <0>To rebook your parcel collection, please click here</0>"
              components={[<P2GLink href={`${websiteUrl}/help-centre/rebook`} />]}
            />
          }
          headingVariant={props.headingVariant}
          {...sharedProps}
        />
      )}

      {!props.hasCancelled && !props.hasAttemptedCollection && !props.hasAddressIssue && (
        <TrackingIssueMessage
          color="info"
          title={t('RESULT_COLLECTION_WAITING_TITLE', 'Waiting for confirmation from the courier')}
          message={t(
            'RESULT_COLLECTION_WAITING',
            'We are waiting for confirmation that the courier has collected your parcel.  If your parcel has already been collected, please allow up to 24hrs for the tracking to update.'
          )}
          headingVariant={props.headingVariant}
          {...sharedProps}
        />
      )}
    </>
  );
};

export default TrackingCollection;
